/* ============================================================
   FILE: packages/volto-wildcard-theme/src/components/Header/Header.tsx
   ============================================================ */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Segment } from 'semantic-ui-react';
import { useSelector } from 'react-redux';

import NavigationDropdown from '../../Navigation/NavigationDropdown';
import SearchWidget from '@plone/volto/components/theme/SearchWidget/SearchWidget';

import themeLogo from '../../../static/img/castle-logo-white.png';

interface HeaderProps {
  pathname: string;
}

const BODY_BASE_CLASS = 'has-fixed-header';
const BODY_FIXED_ACTIVE_CLASS = 'has-fixed-header--active';
const BODY_MOBILE_NAV_OPEN_CLASS = 'has-mobile-nav-open';

const FIX_OFFSET_PX = 40;
const EXIT_ANIM_MS = 260;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const getHeroBottomY = (): number => {
  const hero = document.querySelector<HTMLElement>('.hp-hero');
  if (!hero) return 0;
  const rect = hero.getBoundingClientRect();
  return (window.scrollY || 0) + rect.bottom;
};

const Header: React.FC<HeaderProps> = ({ pathname }) => {
  const isLoggedIn = useSelector((state: any) => !!state.userSession?.token);

  const [scrolled, setScrolled] = useState(false);

  const [fixed, setFixed] = useState(false);
  const [entering, setEntering] = useState(false);
  const [shown, setShown] = useState(false);
  const [exiting, setExiting] = useState(false);

  const [showGoTop, setShowGoTop] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const searchPanelRef = useRef<HTMLDivElement | null>(null);

  // Our own mobile-nav state (no DOM inference)
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const rafId = useRef<number | null>(null);
  const enterRaf = useRef<number | null>(null);
  const exitTimer = useRef<number | null>(null);

  const heroBottomYRef = useRef<number>(0);
  const prevFixedRef = useRef<boolean>(false);

  const recalcHero = useCallback(() => {
    heroBottomYRef.current = getHeroBottomY();
  }, []);

  // Close panels on route change
  useEffect(() => {
    setSearchOpen(false);
    setMobileNavOpen(false);
  }, [pathname]);

  // Reflect mobile-nav state to <body> as a class ONLY (no inline styles)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle(BODY_MOBILE_NAV_OPEN_CLASS, mobileNavOpen);
    return () => {
      document.body.classList.remove(BODY_MOBILE_NAV_OPEN_CLASS);
    };
  }, [mobileNavOpen]);

  // Focus search input when opening
  useEffect(() => {
    if (!searchOpen) return;
    window.setTimeout(() => {
      const root = searchPanelRef.current;
      if (!root) return;
      const input =
        root.querySelector<HTMLInputElement>('input[type="text"]') ||
        root.querySelector<HTMLInputElement>('input');
      input?.focus();
    }, 0);
  }, [searchOpen]);

  // ESC closes search + mobile nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setSearchOpen(false);
      setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /**
   * Mobile menu tracking (event delegation, no DOM reads):
   * - hamburger click toggles our state
   * - close button click closes
   * - clicking any link inside #navigation closes
   *
   * This avoids observers/polling and avoids writing body.style,
   * which is what commonly triggers "infinite loading"/thrash.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isLoggedIn) return;

    const onDocClickCapture = (ev: MouseEvent) => {
      const target = ev.target as Element | null;
      if (!target) return;

      const navRoot = target.closest('#navigation');

      // Only care about clicks within navigation UI
      if (!navRoot) return;

      const isHamburger = !!target.closest('#navigation button.hamburger');
      if (isHamburger) {
        setMobileNavOpen((v) => !v);
        return;
      }

      const isCloseButton = !!target.closest(
        '#navigation button[aria-label*="close" i], #navigation button[title*="close" i]',
      );
      if (isCloseButton) {
        setMobileNavOpen(false);
        return;
      }

      // If user taps a nav link in the drawer, close the drawer.
      const isNavLink = !!target.closest('#navigation a');
      if (isNavLink) {
        setMobileNavOpen(false);
      }
    };

    document.addEventListener('click', onDocClickCapture, true);
    return () => document.removeEventListener('click', onDocClickCapture, true);
  }, [isLoggedIn]);

  // Sticky/fixed header behavior
  useEffect(() => {
    document.body.classList.add(BODY_BASE_CLASS);

    if (isLoggedIn) {
      setScrolled(false);
      setFixed(false);
      setEntering(false);
      setShown(false);
      setExiting(false);
      setShowGoTop(false);

      document.body.classList.remove(BODY_FIXED_ACTIVE_CLASS);

      return () => {
        document.body.classList.remove(BODY_BASE_CLASS);
        document.body.classList.remove(BODY_FIXED_ACTIVE_CLASS);
        document.body.classList.remove(BODY_MOBILE_NAV_OPEN_CLASS);
      };
    }

    const headerHeight = () => {
      const el = document.querySelector<HTMLElement>('.header-wrapper.ui.basic.segment');
      return el?.getBoundingClientRect().height ?? 0;
    };

    const enterFixed = () => {
      if (exitTimer.current != null) window.clearTimeout(exitTimer.current);
      setExiting(false);

      setFixed(true);
      setEntering(true);
      setShown(false);
      document.body.classList.add(BODY_FIXED_ACTIVE_CLASS);

      if (enterRaf.current != null) window.cancelAnimationFrame(enterRaf.current);
      enterRaf.current = window.requestAnimationFrame(() => {
        enterRaf.current = null;
        setEntering(false);
        setShown(true);
      });
    };

    const exitFixed = () => {
      if (exitTimer.current != null) window.clearTimeout(exitTimer.current);
      if (enterRaf.current != null) window.cancelAnimationFrame(enterRaf.current);

      setEntering(false);
      setShown(false);
      setExiting(true);

      exitTimer.current = window.setTimeout(() => {
        exitTimer.current = null;
        setExiting(false);
        setFixed(false);
        document.body.classList.remove(BODY_FIXED_ACTIVE_CLASS);
      }, EXIT_ANIM_MS);
    };

    const measure = () => {
      const y = window.scrollY || 0;

      setScrolled(y > 0);
      setShowGoTop(y >= heroBottomYRef.current);

      const threshold = Math.max(0, heroBottomYRef.current - headerHeight() + FIX_OFFSET_PX);
      const nextFixed = y >= threshold;

      if (nextFixed !== prevFixedRef.current) {
        prevFixedRef.current = nextFixed;
        nextFixed ? enterFixed() : exitFixed();
      }
    };

    const onScroll = () => {
      if (rafId.current != null) return;
      rafId.current = window.requestAnimationFrame(() => {
        rafId.current = null;
        measure();
      });
    };

    const onResize = () => {
      recalcHero();
      measure();
    };

    recalcHero();
    measure();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    let ro: ResizeObserver | null = null;
    const hero = document.querySelector<HTMLElement>('.hp-hero');
    if (hero && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => {
        recalcHero();
        measure();
      });
      ro.observe(hero);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);

      if (rafId.current != null) window.cancelAnimationFrame(rafId.current);
      if (enterRaf.current != null) window.cancelAnimationFrame(enterRaf.current);
      if (exitTimer.current != null) window.clearTimeout(exitTimer.current);

      if (ro) ro.disconnect();

      document.body.classList.remove(BODY_BASE_CLASS);
      document.body.classList.remove(BODY_FIXED_ACTIVE_CLASS);
    };
  }, [recalcHero, isLoggedIn]);

  const onGoTop = () => {
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    window.scrollTo({ top: 0, behavior });
  };

  const headerCls = [
    'header-wrapper',
    scrolled ? 'is-scrolled' : '',
    fixed ? 'is-fixed' : '',
    entering ? 'is-fixed--entering' : '',
    shown ? 'is-fixed--shown' : '',
    exiting ? 'is-fixed--exiting' : '',
    searchOpen ? 'has-search-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Segment basic className={headerCls} role="banner">
        <Container>
          <div className="header">
            <div className="header-left">
              <a className="header-logoLink" href="/" aria-label="Home">
                <img className="header-logoImg" src={themeLogo} alt="Castle CMS" />
                <span className="header-brand">CASTLE CMS</span>
              </a>
            </div>

            <div className="header-right">
              <NavigationDropdown pathname={pathname} root="/" depth={4} />

              <button
                type="button"
                className="header-searchToggle"
                aria-label={searchOpen ? 'Close search' : 'Open search'}
                aria-expanded={searchOpen}
                aria-controls="header-search-panel"
                onClick={() => setSearchOpen((v) => !v)}
              >
                <i className="ion-ios-search" aria-hidden="true" />
              </button>

              <a className="header-cta white" href="https://github.com/castlecms/" target='_blank' aria-label="Download">
                <span className="header-ctaText">DOWNLOAD</span>
                <i className="ion-ios-cloud-download-outline" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div
            id="header-search-panel"
            ref={searchPanelRef}
            className={`header-searchPanel ${searchOpen ? 'is-open' : ''}`}
            aria-hidden={!searchOpen}
          >
            <div className="header-searchInner">
              <SearchWidget />
            </div>
          </div>
        </Container>
      </Segment>

      {!isLoggedIn && (
        <button
          type="button"
          className={`go-top ${showGoTop ? 'is-visible' : ''}`.trim()}
          onClick={onGoTop}
          aria-label="Go to top"
          title="Go to top"
        >
          <i className="ion-ios-arrow-up" aria-hidden="true" />
        </button>
      )}
    </>
  );
};

export default Header;